// src/components/common/OfflineIndicator.tsx
// 離線狀態指示器 - PWA 離線優先體驗

import React from 'react';
import { createPortal } from 'react-dom';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

interface OfflineIndicatorProps {
  /** 顯示位置 */
  position?: 'top' | 'bottom';
  /** 自訂離線訊息 */
  offlineMessage?: string;
  /** 自訂恢復連線訊息 */
  reconnectedMessage?: string;
}

/**
 * 離線狀態指示器
 *
 * 功能：
 * - 離線時顯示黃色警告條
 * - 恢復連線時短暫顯示綠色提示
 * - 自動隱藏/顯示
 */
export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  position = 'bottom',
  offlineMessage = 'You are offline. Changes will sync when connected.',
  reconnectedMessage = 'Back online!'
}) => {
  const { isOnline, justReconnected } = useNetworkStatus();

  // 在線且不是剛恢復連線時，不顯示
  if (isOnline && !justReconnected) {
    return null;
  }

  const positionClasses = position === 'top'
    ? 'top-0'
    : 'bottom-0';

  const content = (
    <div
      className={`
        fixed left-0 right-0 ${positionClasses} z-offline-indicator
        flex items-center justify-center gap-2
        px-4 py-2 text-sm font-medium
        transition-all duration-300 ease-out
        ${isOnline
          ? 'bg-green-500 text-white'
          : 'bg-amber-500 text-white'
        }
      `}
      role="status"
      aria-live="polite"
    >
      {isOnline ? (
        <>
          {/* 連線圖標 */}
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span>{reconnectedMessage}</span>
        </>
      ) : (
        <>
          {/* 離線圖標 */}
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
            />
          </svg>
          <span>{offlineMessage}</span>
        </>
      )}
    </div>
  );

  // 使用 Portal 確保顯示在最上層
  return createPortal(content, document.body);
};

export default OfflineIndicator;
